//------------------------------------------------------------------------------
// BFA10101G 共架申込一覧
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, getFormValues } from 'redux-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import withRouter from '@/components/organisms/withRouter';
import PageTitle from '@/components/molecules/PageTitle';
import MessageArea from '@/components/molecules/WarningMessageArea';
import AppLayout from '@/layouts/app-layout';
import CustomFlexGrid from '@/components/molecules/CustomFlexGrid';
import commonOperations from '@/reducks/common/operations';
import backend from '@/common/backend';
import gridItems from './order-items';
import SearchPanel from './search-panel';
import OperationPanel from './operations';
import SearchConditions from './search-conditions';

// 定数
const FORM_NAME = 'BFA10101G';
const PAGE_NAME = '共架申込一覧';

// 画面項目初期値
const INITIAL_STATE = {
  orderStatus: ['01', '02', '03'],
  temotiKasyo: '共架者',
};

/**
 * 共架申込一覧画面
 */
class OrderList extends React.Component {
  /**
   * コンストラクタ
   */
  constructor(props) {
    super(props);
    this.state = {
      warnings: [],           // 警告メッセージ
      panelExpanded: true,    // 検索パネル開閉状態
      orderSelected: false,   // グリッド行選択状態
    };

    // 申込一覧グリッド
    this.gridRef = React.createRef();
    this.gridView = gridItems.createComponents({
      orderNo: {
        click: this.handleOrderLink,
      },
    });

    // 申込一覧グリッド属性
    this.gridProps = {
      headersVisibility: 'Column',   // 列ヘッダー表示
      filterOn: true,                // フィルタ有効
      AddDeleteOn: false,            // 追加削除無効
      allowSorting: 'MultiColumn',   // 複数列ソート
      allowDragging: 'Columns',      // 列ドラッグ可
      rowHeaderType: 'radio',        // 単一行選択
      isReadOnly: true,              // 編集不可
      style: {},                     // 一覧サイズ可変

      // イベント
      checkedFunction: this.handleSelect,
    };
  }

  /**
   * マウント
   */
  async componentDidMount() {

    // 画面項目初期化 - 初回のみ
    if (!this.props.initialized) {
      await this.initFormValues();
    }

    // 検索実行
    this.handleSearch();
  }

  /**
   * 画面項目初期化
   */
  async initFormValues() {

    // メッセージクリア
    this.setState({ warnings: [] });

    // フォーム項目初期化
    await this.props.reset();
    await this.props.initialize(INITIAL_STATE);
  }
 
  /**
   * 描画
   */
  render() {
    const {
      values,
      change,
      handleSubmit,
    } = this.props;

    return (
      <AppLayout>

        {/* ページタイトル */}
        <PageTitle pageTitle={PAGE_NAME} />

        {/* メッセージ領域 */}
        <MessageArea messages={this.state.warnings}/>

        {/* 検索パネル */}
        <Box>
        <form noValidate autoComplete="off">
          <SearchPanel
            expanded={this.state.panelExpanded}
            handleClear={this.handleClear}
            handleSearch={handleSubmit(this.handleSearch)}
            handleExpand={this.handleExpand}
          >
            {/* 検索条件 */}
            <SearchConditions
              values={values}
              change={change}
            />
          </SearchPanel>
        </form>
        </Box>

        {/* 一覧表示エリア - 可変サイズ */}
        <Stack id="list-area"
          direction="column"
          sx={{
            mt: 1,
            flex: 1,
            minHeight: 0,
            height: '100%',
          }}
        >
          <CustomFlexGrid ref={this.gridRef} {...this.gridProps}>
            {this.gridView}
          </CustomFlexGrid>
        </Stack>

        {/* 操作パネル - フッター固定 */}
        <Box>
          <OperationPanel
            editable={this.state.orderSelected}
            exportable={false}
            handleCreate={this.handleCreate}
            handleExport={this.handleExport}
            handleEdit={this.handleEdit}
            handleBack={this.handleBack}
          />
        </Box>
      </AppLayout>
    );
  }

  //----------------------------------------------------------------------------
  // イベントハンドラー
  //----------------------------------------------------------------------------
  /** 検索 */
  handleSearch = async () => {
    const {
      values,
      doShowMessage,
    } = this.props;

    // 検索処理
    const res = await backend.post('bfa10101g/orders', values);

    // 応答解析
    const orders = res
        .parse(doShowMessage)
        .parseBodyAsArray({ max: 2000 });
    this.setState({ warnings: res.messages });
    console.info('申込一覧', `件数=${orders.length}`);

    // グリッド設定
    const grid = this.gridRef.current;
    if (grid) {
      grid.clearSelectedState();
      grid.setItemsSource(orders);
    }

    // 検索パネルを閉じる
    this.setState({ panelExpanded: false });
  };

  /** 検索条件クリア */
  handleClear = () => {
    this.initFormValues();
  };

  /** 検索パネル開閉 */
  handleExpand = () => {
    this.setState((prev) => ({
      panelExpanded: !prev.panelExpanded,
    }));
  }

  /** 申込番号リンク */
  handleOrderLink = (item) => {
    console.info('申込表示', item);

    // 申込基本情報画面へ遷移
    const link = `/bfa10102g/${item.orderNo}/${item.rev}`;
    this.props.navigate(link);
  }

  /** 一覧行選択 */
  handleSelect = () => {
    const selectedItem = this.gridRef.current
        ?.selectedItems
        ?.at(0);
    const selected = Boolean(selectedItem);
    this.setState({ orderSelected: selected });
  };

  /** 新規作成 */
  handleCreate = () => {
    console.info('新規申込');
    this.props.navigate('/bfa10102g');
  };

  /** 編集 */
  handleEdit = () => {

    // 選択データ取得
    const selectedItem = this.gridRef.current
        ?.selectedItems
        ?.at(0);
    const orderNo = selectedItem.dataItem?.orderNo;
    const orderRev = selectedItem.dataItem?.rev;
    console.info(`Click edit orderNo=${orderNo}-${orderRev}`);

    // 申込基本情報画面へ遷移
    if (orderNo && orderRev) {
      this.handleOrderLink({
        orderNo: orderNo,
        rev: orderRev
      });
    }
  };

  /** 一覧出力 */
  handleExport = () => {
  };

  /** 戻る */
  handleBack = () => {
    this.props.navigate('/');
  };
}

// プロパティ
OrderList.propTypes = {

  // フォーム
  reset: PropTypes.func,
  change: PropTypes.func,
  values: PropTypes.object,
  initialize: PropTypes.func,
  initialized: PropTypes.bool,
  handleSubmit: PropTypes.func,

  // ルーティング
  navigate: PropTypes.func,

  // ストア
  doShowMessage: PropTypes.func,
};

// フォーム接続
OrderList = reduxForm({
  form: FORM_NAME,
  destroyOnUnmount: false,
})(connect((state) => ({
  values: getFormValues(FORM_NAME)(state),
}))(OrderList));

// ルーティング
OrderList = withRouter(OrderList);

// ストア接続
const mapStateToProps = (state) => ({});
const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
};
OrderList = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderList);

// エクスポート
export default OrderList;
